(function () {
  'use strict';

  // ─── Tweakables ──────────────────────────────────────────────────────────────
  const BUTTON_ROW_OFFSET   = 120;    // px below wheel centre → button row (desktop)
  const MOBILE_ROW_OFFSET   = 70;     // px below wheel centre → button row (mobile)
  const PREVIEW_ABOVE_ROW   = -16;    // negative = preview bottom overlaps button row top by 16 px (5 art-px × 4 = 20 px lower than before)
  const WAVE_AMP_PX         = 10;
  const WAVE_SPEED          = 0.002;
  const WAVE_PHASE_STEP     = 0.8;
  const WAVE_SNAP_PX        = 4;
  const MOBILE_WAVE_X_AMP   = 64;
  const MOBILE_WAVE_X_SPEED = 0.0015;
  const MOBILE_WAVE_X_PHASE = 1.2;
  const MOBILE_WAVE_X_SNAP  = 2;
  const MQ_MOBILE           = '(max-width: 1030px)';

  // ─── Helpers ─────────────────────────────────────────────────────────────────
  const qs  = (s, r) => (r || document).querySelector(s);
  const qsa = (s, r) => Array.from((r || document).querySelectorAll(s));
  const snap = (v, s) => Math.round(v / (s || WAVE_SNAP_PX)) * (s || WAVE_SNAP_PX);
  const mql  = window.matchMedia(MQ_MOBILE);

  // ─── Layout positioning ───────────────────────────────────────────────────────
  function placeRow() {
    const row   = qs('#linksButtons');
    const wheel = qs('#dalichromeWheel');
    if (!row || !wheel) return;

    const offset = mql.matches ? MOBILE_ROW_OFFSET : BUTTON_ROW_OFFSET;
    const parent = row.offsetParent || document.body;
    const wr = wheel.getBoundingClientRect();
    const pr = parent.getBoundingClientRect();

    row.style.top        = Math.round(wr.top + wr.height / 2 - pr.top + offset) + 'px';
    row.style.visibility = 'visible';
  }

  function placePreview() {
    const row  = qs('#linksButtons');
    const prev = qs('#linksPreview');
    if (!row || !prev) return;

    // Size to an exact pixel-art multiple (4× desktop, 2× mobile).
    // Preview is CSS-hidden on mobile but we still compute correctly here.
    if (prev.naturalWidth) {
      const s = mql.matches ? 2 : 4;
      prev.style.width  = (prev.naturalWidth  * s) + 'px';
      prev.style.height = (prev.naturalHeight * s) + 'px';
    }

    const parent = prev.offsetParent || document.body;
    const pr = parent.getBoundingClientRect();
    const rr = row.getBoundingClientRect();

    // #linksPreview has transform:translate(-50%,-50%) so `top` = vertical centre of element.
    // PREVIEW_ABOVE_ROW is the gap between the preview's bottom edge and the button row top.
    // Negative value = preview overlaps into the row from above.
    // bottom_edge = centre + prevH/2  →  centre = rowTop - PREVIEW_ABOVE_ROW - prevH/2
    const prevH  = prev.offsetHeight || 96;
    const rowTop = rr.top - pr.top;
    prev.style.top = Math.round(rowTop - PREVIEW_ABOVE_ROW - prevH / 2) + 'px';
  }

  // ─── Preview swap (data-preview → #linksPreview) ──────────────────────────────
  //  Same mechanism as the homepage #preview system.
  //  data-preview on each .links-button-link points to a *_word.png file.
  function setupPreview() {
    const container = qs('#linksButtons');
    const prev      = qs('#linksPreview');
    if (!container || !prev) return;

    // Size + position after load, then reveal — image is never visible before
    // naturalWidth * scale has been applied.
    prev.addEventListener('load', () => {
      placePreview();
      prev.classList.add('is-visible');
    });

    function show(a) {
      const src = a.dataset.preview;
      if (!src) return;
      if (prev.dataset.activeSrc !== src) {
        // New image — start loading; is-visible comes from the load event
        prev.src               = src;
        prev.dataset.activeSrc = src;
      } else {
        // Same image already loaded and sized — just reveal it
        prev.classList.add('is-visible');
      }
    }
    function hide() { prev.classList.remove('is-visible'); }

    // Use capture so mouseover fires even when the <img> inside is the target
    container.addEventListener('mouseover',  e => { const a = e.target.closest('.links-button-link'); if (a) show(a); }, true);
    container.addEventListener('mouseleave', hide);
    container.addEventListener('focusin',    e => { const a = e.target.closest('.links-button-link'); if (a) show(a); });
    container.addEventListener('focusout',   hide);
  }

  // ─── Desktop icon row: hide items that wrap to a second row ─────────────────
  // On desktop the 6 icons are in a flex-wrap row.  When the viewport isn't wide
  // enough to fit them all, the last item(s) drop to a second line and look broken.
  // This function reveals all items first, then hides any that wrapped.
  function clipWrappedButtons() {
    const items = qsa('.links-button');
    if (mql.matches) {
      // Switching to mobile — un-hide anything the desktop clip may have set,
      // so the mobile button list and the small-screen icon grid both see all items.
      items.forEach(el => { el.hidden = false; });
      return;
    }
    if (items.length < 2) return;

    items.forEach(el => { el.hidden = false; }); // restore before re-measuring

    const refTop = items[0].offsetTop;
    for (let i = 1; i < items.length; i++) {
      if (items[i].offsetTop > refTop + 4) {    // +4 px for sub-pixel rounding
        for (let j = i; j < items.length; j++) items[j].hidden = true;
        return;
      }
    }
  }

  // ─── Wave animation ───────────────────────────────────────────────────────────
  let rafD = null, rafM = null;

  function startDesktopWave() {
    const items = qsa('.links-button');
    if (!items.length) return;
    const t0 = performance.now();
    function tick(now) {
      const t = now - t0;
      for (let i = 0; i < items.length; i++) {
        items[i].style.setProperty('--wave-y', snap(Math.sin(t * WAVE_SPEED + i * WAVE_PHASE_STEP) * WAVE_AMP_PX) + 'px');
        items[i].style.setProperty('--wave-x', '0px');
      }
      rafD = requestAnimationFrame(tick);
    }
    rafD = requestAnimationFrame(tick);
  }
  function stopDesktopWave() { if (rafD) { cancelAnimationFrame(rafD); rafD = null; } }

  function startMobileWave() {
    // Target the mobile button items (.links-mobile-item), not the desktop icon items.
    const items = qsa('.links-mobile-item');
    if (!items.length) return;
    const t0 = performance.now();
    function tick(now) {
      const t = now - t0;
      // Half amplitude at the x1 tier (<500px) so buttons sway proportionally less
      const amp = window.innerWidth < 500 ? MOBILE_WAVE_X_AMP / 2 : MOBILE_WAVE_X_AMP;
      for (let i = 0; i < items.length; i++) {
        items[i].style.setProperty('--wave-x', snap(Math.sin(t * MOBILE_WAVE_X_SPEED + i * MOBILE_WAVE_X_PHASE) * amp, MOBILE_WAVE_X_SNAP) + 'px');
        items[i].style.setProperty('--wave-y', '0px');
      }
      rafM = requestAnimationFrame(tick);
    }
    rafM = requestAnimationFrame(tick);
  }
  function stopMobileWave() { if (rafM) { cancelAnimationFrame(rafM); rafM = null; } }

  function applyMode() {
    if (mql.matches) {
      stopDesktopWave();
      // Reset desktop icon wave before switching to mobile
      qsa('.links-button').forEach(el => el.style.setProperty('--wave-y', '0px'));
      startMobileWave();
    } else {
      stopMobileWave();
      // Reset mobile button wave before switching to desktop
      qsa('.links-mobile-item').forEach(el => el.style.setProperty('--wave-x', '0px'));
      startDesktopWave();
    }
    placeRow();
    placePreview();
    clipWrappedButtons();
  }

  // ─── Mobile label scaling ─────────────────────────────────────────────────────
  // Sizes each .links-mobile-label to naturalWidth/Height × scale so it always
  // renders at an exact pixel-art multiple regardless of the image's native size.
  function labelScale() {
    return window.innerWidth < 490 ? 1 : mql.matches ? 2 : 4;
  }
  function scaleMobileLabels() {
    const s = labelScale();
    qsa('.links-mobile-label').forEach(img => {
      if (!img.naturalWidth) return;
      img.style.width  = (img.naturalWidth  * s) + 'px';
      img.style.height = (img.naturalHeight * s) + 'px';
    });
  }
  // Scale each label as soon as it loads (they are loading="eager" so this is
  // usually synchronous by boot time, but the load listener handles late arrivals).
  qsa('.links-mobile-label').forEach(img => {
    if (img.complete && img.naturalWidth) return; // will be handled in boot
    img.addEventListener('load', scaleMobileLabels, { once: true });
  });

  // ─── Boot ─────────────────────────────────────────────────────────────────────
  function boot() {
    setupPreview();
    scaleMobileLabels();   // initial sizing (images already loaded eagerly)
    applyMode();

    window.addEventListener('resize', () => { placeRow(); placePreview(); clipWrappedButtons(); scaleMobileLabels(); });

    if (mql.addEventListener) mql.addEventListener('change', applyMode);
    else                       mql.addListener(applyMode);   // Safari <14

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) { stopDesktopWave(); stopMobileWave(); }
      else applyMode();
    });
  }

  const wheel = document.getElementById('dalichromeWheel');
  if (!wheel) return;
  if (wheel.complete && wheel.naturalWidth > 0) boot();
  else wheel.addEventListener('load', boot, { once: true });

})();
